import { useQueryClient } from '@tanstack/react-query';
import type { StaffConversationListItem } from '@zrsystem/contracts';
import {
  Avatar,
  Badge,
  Button,
  Empty,
  EmptyIcon,
  EmptyText,
  EmptyTitle,
  ListRow,
  ListRowAside,
  ListRowAsideValue,
  ListRowMain,
  ListRowSubtitle,
  ListRowTitle,
  ListRows,
  listTablePrimaryInitials,
  ShellIconChevronLeft,
  ShellIconChevronRight,
  ShellIconEdit,
  ShellIconTrash,
  ShellIconUsers,
  ShellIconX,
  Spinner,
  SplitWorkbenchLayout,
  useBreakpoint,
  useIsPhone,
  VideoPlayer,
} from '@zrsystem/ui';
import { stripChatInlineBold } from '@zrsystem/utils';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { AppointmentConfirmDialog } from '@/features/appointment/components/appointment-confirm-dialog.js';
import {
  createConversationSocket,
  STAFF_CHAT_EVENTS,
  type StaffChatErrorPayload,
  type StaffChatListUpdatePayload,
  type StaffChatMessage,
} from '@/features/conversation/api/conversation-socket.js';
import { uploadStaffChatMedia } from '@/features/conversation/api/staff-conversation-attachments.api.js';
import { ChatAudioBubble } from '@/features/conversation/components/chat-audio-bubble.js';
import { FormattedChatText } from '@/features/conversation/components/formatted-chat-text.js';
import { StaffChatAttachConfirm } from '@/features/conversation/components/staff-chat-attach-confirm.js';
import { useStaffConversations } from '@/features/conversation/hooks/use-staff-conversations.js';
import { stepGalleryIndex } from '@/features/conversation/lib/chat-image-gallery.js';
import { staffConversationKeys } from '@/features/conversation/query-keys.js';
import '@/features/conversation/conversation.css';
import { HttpError } from '@/shared/api/types.js';
import { getAccessToken } from '@/shared/auth/auth-storage.js';
import { ChatComposer } from '@/shared/chat/chat-composer.js';
import { ChatListSearch } from '@/shared/chat/chat-list-search.js';
import { chatMatchesSearch } from '@/shared/chat/match-chat-query.js';
import { useLongPress } from '@/shared/chat/use-long-press.js';
import { ContextualFeedback } from '@/shared/feedback/index.js';
import { translateError } from '@/shared/i18n/error-messages.js';

interface ListPatch {
  readonly unreadCount?: number;
  readonly lastMessage?: StaffConversationListItem['lastMessage'];
  readonly updatedAt?: string;
}

function StaffChatMessageFrame({
  messageId,
  own,
  canHold,
  actionsOpen,
  onLongPress,
  children,
}: {
  readonly messageId: string;
  readonly own: boolean;
  readonly canHold: boolean;
  readonly actionsOpen: boolean;
  readonly onLongPress: () => void;
  readonly children: ReactNode;
}): ReactElement {
  const { pressing, ...pressHandlers } = useLongPress(onLongPress, canHold);
  const className = [
    'staff-chat-inbox__message',
    own ? 'staff-chat-inbox__message--own' : '',
    pressing ? 'staff-chat-inbox__message--holding' : '',
    actionsOpen ? 'staff-chat-inbox__message--actions-open' : '',
  ]
    .filter((item) => item.length > 0)
    .join(' ');

  return (
    <div className={className} data-staff-message-id={messageId} {...pressHandlers}>
      {children}
    </div>
  );
}

function previewText(message: StaffConversationListItem['lastMessage']): string {
  if (message === null) {
    return 'Nenhuma mensagem ainda';
  }
  if (message.kind === 'audio') {
    return 'Áudio';
  }
  if (message.kind === 'image' || message.kind === 'video') {
    const trimmed = message.text?.trim();
    if (trimmed !== undefined && trimmed.length > 0) {
      return stripChatInlineBold(trimmed);
    }
    return message.kind === 'video' ? 'Vídeo' : 'Foto';
  }
  return stripChatInlineBold(message.text ?? '');
}

function formatListTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function formatMessageTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function StaffChatInboxPresenter({ stacked = false }: { readonly stacked?: boolean }) {
  const queryClient = useQueryClient();
  const isPhone = useIsPhone();
  const compact = stacked || isPhone;
  const breakpoint = useBreakpoint();
  const asideWidth = breakpoint === 'tablet' ? 260 : 320;
  const listQuery = useStaffConversations();
  const [patches, setPatches] = useState<Record<string, ListPatch>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [chatSearch, setChatSearch] = useState('');
  const [messages, setMessages] = useState<StaffChatMessage[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [actionsMessageId, setActionsMessageId] = useState<string | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketErrorMessage, setSocketErrorMessage] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<
    { file: File; previewUrl: string; isVideo: boolean }[]
  >([]);
  const [pendingIndex, setPendingIndex] = useState(0);

  useEffect(() => {
    if (actionsMessageId === null) {
      return;
    }
    const close = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest(`[data-staff-message-id="${actionsMessageId}"]`) !== null) {
        return;
      }
      setActionsMessageId(null);
    };
    document.addEventListener('pointerdown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
    };
  }, [actionsMessageId]);

  const rawItems = listQuery.data?.items ?? [];
  const rawItemsRef = useRef(rawItems);
  const selectedIdRef = useRef<string | null>(null);
  const socketRef = useRef<ReturnType<typeof createConversationSocket> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const pendingPreviewRef = useRef<string[]>([]);
  const pendingCountRef = useRef(0);
  pendingPreviewRef.current = pendingFiles.map((item) => item.previewUrl);
  pendingCountRef.current = pendingFiles.length;

  const handleVoiceSend = useCallback(
    async (recorded: { file: File; durationSeconds: number }): Promise<boolean> => {
      if (selectedId === null) {
        return false;
      }
      setUploading(true);
      setAttachError(null);
      try {
        const socket = socketRef.current;
        if (socket?.connected !== true) {
          setAttachError('Conecte-se ao chat para enviar anexos');
          return false;
        }
        const uploaded = await uploadStaffChatMedia(selectedId, recorded.file);
        socket.emit(STAFF_CHAT_EVENTS.send, {
          conversationId: selectedId,
          kind: 'audio',
          storageKey: uploaded.storageKey,
          contentType: uploaded.contentType,
          durationSeconds: recorded.durationSeconds,
        });
        return true;
      } catch (error: unknown) {
        if (error instanceof HttpError) {
          setAttachError(translateError(error.code));
        } else {
          setAttachError(error instanceof Error ? error.message : 'Falha ao enviar áudio');
        }
        return false;
      } finally {
        setUploading(false);
      }
    },
    [selectedId],
  );

  const items = useMemo(() => {
    const merged = rawItems.map((item) => {
      const patch = patches[item.conversationId];
      if (patch === undefined) {
        return item;
      }
      return {
        ...item,
        ...(patch.unreadCount !== undefined ? { unreadCount: patch.unreadCount } : {}),
        ...(patch.lastMessage !== undefined ? { lastMessage: patch.lastMessage } : {}),
        ...(patch.updatedAt !== undefined ? { updatedAt: patch.updatedAt } : {}),
      };
    });
    return [...merged].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }, [rawItems, patches]);

  const selected = useMemo(
    () => items.find((item) => item.conversationId === selectedId) ?? null,
    [items, selectedId],
  );
  const searching = chatSearch.trim().length > 0;
  const visibleItems = useMemo(
    () =>
      items.filter((item) =>
        chatMatchesSearch(chatSearch, [item.consumerDisplayName, previewText(item.lastMessage)]),
      ),
    [chatSearch, items],
  );

  const errorMessage =
    listQuery.error instanceof HttpError ? translateError(listQuery.error.code) : null;

  useEffect(() => {
    rawItemsRef.current = rawItems;
  }, [rawItems]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  // Persistent socket for the page lifetime — server auto-joins the tenant room (list-update).
  useEffect(() => {
    const token = getAccessToken();
    if (token === null) {
      setSocketErrorMessage('Sessão necessária para o chat');
      return;
    }

    const socket = createConversationSocket(token);
    socketRef.current = socket;

    socket.on('connect', () => {
      setSocketConnected(true);
      setSocketErrorMessage(null);
      const current = selectedIdRef.current;
      if (current !== null) {
        socket.emit(STAFF_CHAT_EVENTS.join, { conversationId: current });
      }
    });

    socket.on('disconnect', () => {
      setSocketConnected(false);
    });

    socket.on(STAFF_CHAT_EVENTS.history, (history: StaffChatMessage[]) => {
      setMessages(Array.isArray(history) ? history : []);
      setHistoryLoaded(true);
    });

    socket.on(STAFF_CHAT_EVENTS.new, (message: StaffChatMessage) => {
      if (message.conversationId === selectedIdRef.current) {
        setMessages((prev) =>
          prev.some((existing) => existing.id === message.id) ? prev : [...prev, message],
        );
        if (message.author.kind === 'consumer') {
          socket.emit(STAFF_CHAT_EVENTS.ack, { conversationId: message.conversationId });
        }
      }
    });

    const applyMessageUpdate = (updated: StaffChatMessage): void => {
      if (updated.conversationId !== selectedIdRef.current) return;
      setMessages((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    };
    socket.on(STAFF_CHAT_EVENTS.edited, applyMessageUpdate);
    socket.on(STAFF_CHAT_EVENTS.deleted, applyMessageUpdate);

    socket.on(STAFF_CHAT_EVENTS.listUpdate, (payload: StaffChatListUpdatePayload) => {
      const isOpen = payload.conversationId === selectedIdRef.current;
      const known = rawItemsRef.current.some(
        (item) => item.conversationId === payload.conversationId,
      );
      if (!known) {
        void queryClient.invalidateQueries({ queryKey: staffConversationKeys.all });
        if (payload.authorKind === 'consumer') {
          void queryClient.invalidateQueries({ queryKey: ['notifications', 'staff'] });
        }
        return;
      }
      setPatches((prev) => {
        const baseUnread =
          prev[payload.conversationId]?.unreadCount ??
          rawItemsRef.current.find((item) => item.conversationId === payload.conversationId)
            ?.unreadCount ??
          0;
        const nextUnread = isOpen || payload.authorKind === 'staff' ? 0 : baseUnread + 1;
        return {
          ...prev,
          [payload.conversationId]: {
            unreadCount: nextUnread,
            lastMessage: {
              kind: payload.kind,
              text: payload.text,
              sentAt: payload.sentAt,
              authorKind: payload.authorKind,
            },
            updatedAt: payload.sentAt,
          },
        };
      });
      if (payload.authorKind === 'consumer' && !isOpen) {
        // Bell socket is a second /socket.io connection; Vite proxy can drop it (EPIPE).
        // Query key mirrors `staffNotificationKeys.all` without a cross-feature import.
        void queryClient.invalidateQueries({ queryKey: ['notifications', 'staff'] });
      }
    });

    socket.on(STAFF_CHAT_EVENTS.error, (payload: StaffChatErrorPayload) => {
      setSocketErrorMessage(payload.message ?? 'Falha no chat');
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [queryClient]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  function handleBackToList(): void {
    setSelectedId(null);
    setLightboxIndex(null);
    setAttachError(null);
    setEditingMessageId(null);
    setDraft('');
    setPendingFiles((current) => {
      for (const item of current) URL.revokeObjectURL(item.previewUrl);
      return [];
    });
    setPendingIndex(0);
  }

  function handleSelect(conversationId: string): void {
    if (conversationId === selectedId) {
      return;
    }
    setSelectedId(conversationId);
    setMessages([]);
    setHistoryLoaded(false);
    setLightboxIndex(null);
    setAttachError(null);
    setEditingMessageId(null);
    setDraft('');
    setPendingFiles((current) => {
      for (const item of current) URL.revokeObjectURL(item.previewUrl);
      return [];
    });
    setPendingIndex(0);
    setPatches((prev) => ({
      ...prev,
      [conversationId]: { ...prev[conversationId], unreadCount: 0 },
    }));
    const socket = socketRef.current;
    if (socket?.connected === true) {
      socket.emit(STAFF_CHAT_EVENTS.join, { conversationId });
    }
  }

  function handleSend(): void {
    const text = draft.trim();
    const socket = socketRef.current;
    if (text.length === 0 || selectedId === null || socket?.connected !== true) {
      return;
    }
    if (editingMessageId !== null) {
      socket.emit(STAFF_CHAT_EVENTS.edit, {
        conversationId: selectedId,
        messageId: editingMessageId,
        text,
      });
      setEditingMessageId(null);
      setDraft('');
      return;
    }
    socket.emit(STAFF_CHAT_EVENTS.send, { conversationId: selectedId, text });
    setDraft('');
  }

  function handleCancelEdit(): void {
    setEditingMessageId(null);
    setDraft('');
  }

  function handleStartEdit(message: StaffChatMessage): void {
    setEditingMessageId(message.id);
    setDraft(message.text ?? '');
  }

  function handleConfirmDelete(): void {
    const socket = socketRef.current;
    const messageId = confirmingDeleteId;
    if (messageId === null || selectedId === null || socket?.connected !== true) {
      setConfirmingDeleteId(null);
      return;
    }
    socket.emit(STAFF_CHAT_EVENTS.delete, { conversationId: selectedId, messageId });
    if (editingMessageId === messageId) {
      setEditingMessageId(null);
      setDraft('');
    }
    setConfirmingDeleteId(null);
  }

  function stageAttachments(files: readonly File[]): void {
    if (selectedId === null || files.length === 0) return;
    setAttachError(null);
    const staged = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      isVideo: file.type.startsWith('video/'),
    }));
    const nextIndex = pendingCountRef.current + staged.length - 1;
    pendingCountRef.current += staged.length;
    setPendingIndex(nextIndex);
    setPendingFiles((current) => [...current, ...staged]);
  }

  function handleAttachCancel(): void {
    if (uploading) return;
    setPendingFiles((current) => {
      for (const item of current) URL.revokeObjectURL(item.previewUrl);
      return [];
    });
    setPendingIndex(0);
  }

  async function handleAttachConfirm(captions: readonly (string | undefined)[]): Promise<void> {
    if (pendingFiles.length === 0 || selectedId === null) {
      return;
    }
    const socket = socketRef.current;
    if (socket?.connected !== true) {
      setAttachError('Conecte-se ao chat para enviar anexos');
      return;
    }
    setUploading(true);
    setAttachError(null);
    const kept: typeof pendingFiles = [];
    let failed = false;
    for (let index = 0; index < pendingFiles.length; index += 1) {
      const item = pendingFiles[index];
      if (item === undefined) continue;
      if (failed) {
        kept.push(item);
        continue;
      }
      const caption = captions[index];
      try {
        const uploaded = await uploadStaffChatMedia(selectedId, item.file);
        socket.emit(STAFF_CHAT_EVENTS.send, {
          conversationId: selectedId,
          kind: uploaded.kind,
          storageKey: uploaded.storageKey,
          contentType: uploaded.contentType,
          ...(caption !== undefined ? { caption } : {}),
        });
        URL.revokeObjectURL(item.previewUrl);
      } catch (error: unknown) {
        failed = true;
        if (error instanceof HttpError) {
          setAttachError(translateError(error.code));
        } else {
          setAttachError('Falha ao enviar anexo');
        }
        kept.push(item);
      }
    }
    setPendingFiles(kept);
    setPendingIndex(0);
    setUploading(false);
  }

  const galleryUrls = useMemo(() => {
    const items: { id: string; url: string; alt: string }[] = [];
    for (const message of messages) {
      if (message.kind !== 'image') continue;
      const url = message.imageUrl;
      if (url === undefined || url.length === 0) continue;
      items.push({
        id: message.id,
        url,
        alt: message.text ?? 'Foto enviada no chat',
      });
    }
    return items;
  }, [messages]);

  const lightboxItem = lightboxIndex === null ? null : (galleryUrls[lightboxIndex] ?? null);

  useEffect(() => {
    if (lightboxIndex === null) {
      return;
    }
    if (galleryUrls.length === 0) {
      setLightboxIndex(null);
      return;
    }
    if (lightboxIndex >= galleryUrls.length) {
      setLightboxIndex(galleryUrls.length - 1);
    }
  }, [galleryUrls, lightboxIndex]);

  useEffect(() => {
    return () => {
      for (const url of pendingPreviewRef.current) {
        URL.revokeObjectURL(url);
      }
    };
  }, []);

  useEffect(() => {
    if (pendingFiles.length === 0) {
      return;
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape' && !uploading) {
        handleAttachCancel();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [pendingFiles.length, uploading]);

  useEffect(() => {
    if (lightboxIndex === null) {
      return;
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setLightboxIndex(null);
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setLightboxIndex((current) =>
          current === null ? null : stepGalleryIndex(current, galleryUrls.length, -1),
        );
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setLightboxIndex((current) =>
          current === null ? null : stepGalleryIndex(current, galleryUrls.length, 1),
        );
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [lightboxIndex, galleryUrls.length]);

  const rail = (
    <div className="staff-chat-inbox__rail">
      <div className="staff-chat-inbox__rail-header">
        <ShellIconUsers size={16} />
        <strong>Conversas</strong>
      </div>
      <ChatListSearch value={chatSearch} onChange={setChatSearch} />
      <div className="staff-chat-inbox__rail-scroll">
        {listQuery.isPending ? <Spinner aria-label="Carregando conversas" /> : null}
        {errorMessage ? <ContextualFeedback variant="danger" message={errorMessage} /> : null}
        {!listQuery.isPending && !errorMessage && items.length === 0 ? (
          <Empty>
            <EmptyIcon>
              <ShellIconUsers size={22} />
            </EmptyIcon>
            <EmptyTitle>Nenhuma conversa</EmptyTitle>
            <EmptyText>As conversas com clientes do app aparecem aqui.</EmptyText>
          </Empty>
        ) : null}
        {!listQuery.isPending &&
        !errorMessage &&
        searching &&
        items.length > 0 &&
        visibleItems.length === 0 ? (
          <Empty>
            <EmptyTitle>Nenhuma conversa encontrada</EmptyTitle>
            <EmptyText>Tente outro nome ou trecho da conversa.</EmptyText>
          </Empty>
        ) : null}
        {visibleItems.length > 0 ? (
          <ListRows>
            {visibleItems.map((item) => (
              <ListRow
                key={item.conversationId}
                selected={item.conversationId === selectedId}
                onClick={() => {
                  handleSelect(item.conversationId);
                }}
                aria-label={
                  item.unreadCount > 0
                    ? `${item.consumerDisplayName}, ${item.unreadCount} mensagens não lidas`
                    : item.consumerDisplayName
                }
              >
                <Avatar size="lg" aria-hidden>
                  {listTablePrimaryInitials(item.consumerDisplayName)}
                </Avatar>
                <ListRowMain>
                  <ListRowTitle>{item.consumerDisplayName}</ListRowTitle>
                  <ListRowSubtitle>{previewText(item.lastMessage)}</ListRowSubtitle>
                </ListRowMain>
                <ListRowAside>
                  <ListRowAsideValue>{formatListTime(item.updatedAt)}</ListRowAsideValue>
                  {item.unreadCount > 0 ? (
                    <Badge variant="solid-primary">
                      {item.unreadCount > 99 ? '99+' : item.unreadCount}
                    </Badge>
                  ) : null}
                </ListRowAside>
              </ListRow>
            ))}
          </ListRows>
        ) : null}
      </div>
    </div>
  );

  const placeholder = (
    <div className="staff-chat-inbox__placeholder">
      <ShellIconUsers size={36} />
      <EmptyText>Selecione uma conversa para ver as mensagens</EmptyText>
    </div>
  );

  const threadPanel =
    selected === null ? null : (
      <div
        className={
          compact
            ? 'staff-chat-inbox__thread staff-chat-inbox__thread--solo'
            : 'staff-chat-inbox__thread'
        }
      >
        <div className="staff-chat-inbox__thread-header">
          {compact ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconOnly
              className="zr-touch-target"
              aria-label="Voltar para conversas"
              onClick={handleBackToList}
            >
              <ShellIconChevronLeft size={16} />
            </Button>
          ) : null}
          <Avatar size="lg" aria-hidden>
            {listTablePrimaryInitials(selected.consumerDisplayName)}
          </Avatar>
          <strong>{selected.consumerDisplayName}</strong>
        </div>

        <div className="staff-chat-inbox__messages">
          {!historyLoaded ? <Spinner aria-label="Carregando histórico" /> : null}
          {socketErrorMessage ? (
            <ContextualFeedback variant="danger" message={socketErrorMessage} />
          ) : null}
          {historyLoaded && messages.length === 0 ? (
            <EmptyText>Conversa sem mensagens ainda. Envie a primeira.</EmptyText>
          ) : null}
          {messages.map((message) => (
            <StaffChatMessageFrame
              key={message.id}
              messageId={message.id}
              own={message.author.kind === 'staff'}
              canHold={
                message.author.kind === 'staff' && message.kind === 'text' && !message.deletedAt
              }
              actionsOpen={actionsMessageId === message.id}
              onLongPress={() => {
                setActionsMessageId(message.id);
              }}
            >
              {message.author.kind === 'staff' && message.kind === 'text' && !message.deletedAt ? (
                <div className="staff-chat-inbox__message-actions" data-long-press-ignore>
                  <button
                    type="button"
                    className="staff-chat-inbox__message-action-btn"
                    aria-label="Editar mensagem"
                    onClick={() => {
                      handleStartEdit(message);
                    }}
                  >
                    <ShellIconEdit size={14} />
                  </button>
                  <button
                    type="button"
                    className="staff-chat-inbox__message-action-btn"
                    aria-label="Excluir mensagem"
                    onClick={() => {
                      setConfirmingDeleteId(message.id);
                    }}
                  >
                    <ShellIconTrash size={14} />
                  </button>
                </div>
              ) : null}
              {message.kind === 'text' && message.deletedAt ? (
                <p className="staff-chat-inbox__message-text staff-chat-inbox__message-text--deleted">
                  Mensagem apagada
                </p>
              ) : message.kind === 'video' ? (
                <>
                  {message.videoUrl !== undefined && message.videoUrl.length > 0 ? (
                    <VideoPlayer
                      className="staff-chat-inbox__message-video"
                      src={message.videoUrl}
                    />
                  ) : null}
                  {message.text !== null && message.text.length > 0 ? (
                    <p className="staff-chat-inbox__message-text">
                      <FormattedChatText text={message.text} />
                    </p>
                  ) : message.videoUrl === undefined || message.videoUrl.length === 0 ? (
                    <p className="staff-chat-inbox__message-text">Vídeo</p>
                  ) : null}
                </>
              ) : message.kind === 'audio' ? (
                <>
                  {message.audioUrl !== undefined && message.audioUrl.length > 0 ? (
                    <ChatAudioBubble
                      audioUrl={message.audioUrl}
                      messageId={message.id}
                      inverted={message.author.kind === 'staff'}
                      {...(message.durationSeconds !== undefined
                        ? { durationSeconds: message.durationSeconds }
                        : {})}
                    />
                  ) : (
                    <p className="staff-chat-inbox__message-text">Áudio</p>
                  )}
                </>
              ) : message.kind === 'image' ? (
                <>
                  {message.imageUrl !== undefined && message.imageUrl.length > 0 ? (
                    <button
                      type="button"
                      className="staff-chat-inbox__message-image-btn"
                      onClick={() => {
                        const nextIndex = galleryUrls.findIndex((item) => item.id === message.id);
                        setLightboxIndex(nextIndex >= 0 ? nextIndex : 0);
                      }}
                      aria-label="Ampliar imagem"
                    >
                      <img
                        className="staff-chat-inbox__message-image"
                        src={message.imageUrl}
                        alt={message.text ?? 'Foto enviada no chat'}
                        loading="lazy"
                      />
                    </button>
                  ) : null}
                  {message.text !== null && message.text.length > 0 ? (
                    <p className="staff-chat-inbox__message-text">
                      <FormattedChatText text={message.text} />
                    </p>
                  ) : message.imageUrl === undefined || message.imageUrl.length === 0 ? (
                    <p className="staff-chat-inbox__message-text">Foto</p>
                  ) : null}
                </>
              ) : (
                <p className="staff-chat-inbox__message-text">
                  <FormattedChatText text={message.text ?? ''} />
                </p>
              )}
              {message.editedAt && !message.deletedAt ? (
                <span className="staff-chat-inbox__message-edited">editado</span>
              ) : null}
              <span className="staff-chat-inbox__message-time">
                {formatMessageTime(message.sentAt)}
              </span>
            </StaffChatMessageFrame>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {attachError ? (
          <div className="staff-chat-inbox__attach-error">
            <ContextualFeedback variant="danger" message={attachError} />
          </div>
        ) : null}
        {editingMessageId !== null ? (
          <div className="staff-chat-inbox__editing-banner">
            <span>Editando mensagem</span>
            <button
              type="button"
              className="staff-chat-inbox__editing-banner-close"
              aria-label="Cancelar edição"
              onClick={handleCancelEdit}
            >
              <ShellIconX size={14} />
            </button>
          </div>
        ) : null}
        <ChatComposer
          key={selectedId ?? 'none'}
          draft={draft}
          disabled={!socketConnected}
          mediaLocked={editingMessageId !== null}
          isSending={false}
          isUploading={uploading}
          errorMessage={null}
          onError={setAttachError}
          onDraftChange={setDraft}
          onSubmitText={handleSend}
          onSubmitAudio={(file, durationSeconds) => handleVoiceSend({ file, durationSeconds })}
          onAttachFile={(file) => {
            stageAttachments([file]);
          }}
          photoAccept="image/jpeg,image/png,image/webp,image/gif"
          fileAccept="video/mp4"
          fileLabel="Vídeo"
        />

        {pendingFiles.length > 0 ? (
          <StaffChatAttachConfirm
            items={pendingFiles.map((item) => ({
              previewUrl: item.previewUrl,
              fileName: item.file.name,
              isVideo: item.isVideo,
            }))}
            selectedIndex={pendingIndex}
            uploading={uploading}
            onSelect={setPendingIndex}
            onCancel={handleAttachCancel}
            onConfirm={(captions) => {
              void handleAttachConfirm(captions);
            }}
            onAddFiles={stageAttachments}
          />
        ) : null}
        {lightboxItem !== null && lightboxIndex !== null ? (
          <div
            className="staff-chat-inbox__lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Imagem ampliada"
            onClick={() => {
              setLightboxIndex(null);
            }}
          >
            <Button
              type="button"
              variant="ghost"
              className="staff-chat-inbox__lightbox-close"
              aria-label="Fechar imagem"
              onClick={(event) => {
                event.stopPropagation();
                setLightboxIndex(null);
              }}
            >
              Fechar
            </Button>
            {galleryUrls.length > 1 ? (
              <p className="staff-chat-inbox__lightbox-counter" aria-live="polite">
                {lightboxIndex + 1} / {galleryUrls.length}
              </p>
            ) : null}
            {lightboxIndex > 0 ? (
              <Button
                type="button"
                variant="ghost"
                className="staff-chat-inbox__lightbox-nav staff-chat-inbox__lightbox-nav--prev"
                aria-label="Foto anterior"
                onClick={(event) => {
                  event.stopPropagation();
                  setLightboxIndex((current) =>
                    current === null ? null : stepGalleryIndex(current, galleryUrls.length, -1),
                  );
                }}
              >
                <ShellIconChevronLeft size={20} />
              </Button>
            ) : null}
            {lightboxIndex < galleryUrls.length - 1 ? (
              <Button
                type="button"
                variant="ghost"
                className="staff-chat-inbox__lightbox-nav staff-chat-inbox__lightbox-nav--next"
                aria-label="Próxima foto"
                onClick={(event) => {
                  event.stopPropagation();
                  setLightboxIndex((current) =>
                    current === null ? null : stepGalleryIndex(current, galleryUrls.length, 1),
                  );
                }}
              >
                <ShellIconChevronRight size={20} />
              </Button>
            ) : null}
            <figure
              className="staff-chat-inbox__lightbox-figure"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <img
                className="staff-chat-inbox__lightbox-img"
                src={lightboxItem.url}
                alt={lightboxItem.alt}
              />
            </figure>
          </div>
        ) : null}
      </div>
    );

  return (
    <div className="staff-chat-inbox">
      {compact ? (
        selected === null ? (
          rail
        ) : (
          threadPanel
        )
      ) : (
        <SplitWorkbenchLayout
          asideWidth={asideWidth}
          aside={rail}
          className="staff-chat-inbox__split"
        >
          {selected === null ? placeholder : threadPanel}
        </SplitWorkbenchLayout>
      )}
      <AppointmentConfirmDialog
        open={confirmingDeleteId !== null}
        title="Excluir mensagem?"
        message="Essa ação não pode ser desfeita."
        confirmLabel="Excluir"
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setConfirmingDeleteId(null);
        }}
      />
    </div>
  );
}
